/* Real HTTP API implementation of NewsClient */

import { INewsClient } from './INewsClient';
import {
  NewsItemDto,
  NewsCommentDto,
  NewsTagDto,
  NewsEngagementSummaryDto,
  CreateUpdateNewsItemDto,
  CreateUpdateNewsTagDto,
  NewsItemStatus,
} from './newsTypes';

export class NewsClientApi implements INewsClient {
  private baseUrl: string;

  constructor(baseUrl: string = '/api/news') {
    this.baseUrl = baseUrl;
  }

  async getNewsItems(filters?: {
    status?: NewsItemStatus;
    franchiseId?: number;
    storeNumber?: string;
    searchText?: string;
    isPinned?: boolean;
    includeExpired?: boolean;
  }): Promise<NewsItemDto[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.franchiseId) params.append('franchiseId', filters.franchiseId.toString());
    if (filters?.storeNumber) params.append('storeNumber', filters.storeNumber);
    if (filters?.searchText) params.append('searchText', filters.searchText);
    if (filters?.isPinned !== undefined) params.append('isPinned', filters.isPinned.toString());
    if (filters?.includeExpired) params.append('includeExpired', filters.includeExpired.toString());

    const response = await fetch(`${this.baseUrl}/items?${params}`);
    if (!response.ok) throw new Error('Failed to fetch news items');
    return response.json();
  }

  async getNewsItem(id: number): Promise<NewsItemDto | null> {
    const response = await fetch(`${this.baseUrl}/items/${id}`);
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Failed to fetch news item');
    return response.json();
  }

  async createNewsItem(dto: CreateUpdateNewsItemDto): Promise<NewsItemDto> {
    const response = await fetch(`${this.baseUrl}/items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    });
    if (!response.ok) throw new Error('Failed to create news item');
    return response.json();
  }

  async updateNewsItem(id: number, dto: CreateUpdateNewsItemDto): Promise<NewsItemDto> {
    const response = await fetch(`${this.baseUrl}/items/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    });
    if (!response.ok) throw new Error('Failed to update news item');
    return response.json();
  }

  async deleteNewsItem(id: number): Promise<void> {
    const response = await fetch(`${this.baseUrl}/items/${id}`, { method: 'DELETE' });
    if (!response.ok) throw new Error('Failed to delete news item');
  }

  async getComments(newsItemId: number): Promise<NewsCommentDto[]> {
    const response = await fetch(`${this.baseUrl}/items/${newsItemId}/comments`);
    if (!response.ok) throw new Error('Failed to fetch comments');
    return response.json();
  }

  async addComment(newsItemId: number, content: string): Promise<NewsCommentDto> {
    const response = await fetch(`${this.baseUrl}/items/${newsItemId}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    });
    if (!response.ok) throw new Error('Failed to add comment');
    return response.json();
  }

  async deleteComment(commentId: number): Promise<void> {
    const response = await fetch(`${this.baseUrl}/comments/${commentId}`, { method: 'DELETE' });
    if (!response.ok) throw new Error('Failed to delete comment');
  }

  async getEngagementSummary(newsItemId: number): Promise<NewsEngagementSummaryDto> {
    const response = await fetch(`${this.baseUrl}/items/${newsItemId}/engagement`);
    if (!response.ok) throw new Error('Failed to fetch engagement summary');
    return response.json();
  }

  async recordView(newsItemId: number): Promise<void> {
    const response = await fetch(`${this.baseUrl}/items/${newsItemId}/view`, { method: 'POST' });
    if (!response.ok) throw new Error('Failed to record view');
  }

  async toggleLike(newsItemId: number, liked: boolean): Promise<NewsItemDto> {
    const response = await fetch(`${this.baseUrl}/items/${newsItemId}/like`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ liked }),
    });
    if (!response.ok) throw new Error('Failed to toggle like');
    return response.json();
  }

  async getTags(filters?: { activeOnly?: boolean; searchText?: string }): Promise<NewsTagDto[]> {
    const params = new URLSearchParams();
    if (filters?.activeOnly) params.append('activeOnly', 'true');
    if (filters?.searchText) params.append('searchText', filters.searchText);

    const response = await fetch(`${this.baseUrl}/tags?${params}`);
    if (!response.ok) throw new Error('Failed to fetch tags');
    return response.json();
  }

  async getTag(id: number): Promise<NewsTagDto | null> {
    const response = await fetch(`${this.baseUrl}/tags/${id}`);
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Failed to fetch tag');
    return response.json();
  }

  async createTag(dto: CreateUpdateNewsTagDto): Promise<NewsTagDto> {
    const response = await fetch(`${this.baseUrl}/tags`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    });
    if (!response.ok) throw new Error('Failed to create tag');
    return response.json();
  }

  async updateTag(id: number, dto: CreateUpdateNewsTagDto): Promise<NewsTagDto> {
    const response = await fetch(`${this.baseUrl}/tags/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    });
    if (!response.ok) throw new Error('Failed to update tag');
    return response.json();
  }

  async deleteTag(id: number): Promise<void> {
    const response = await fetch(`${this.baseUrl}/tags/${id}`, { method: 'DELETE' });
    if (!response.ok) throw new Error('Failed to delete tag');
  }
}
